import React, { useEffect, useRef, useState } from "react";
import {
  adminLogin, adminVerify, listLeads, updateLeadStatus, deleteLead, adminMetrics,
  listAppointments, updateAppointmentStatus, deleteAppointment,
  listBlockedDates, addBlockedDate, removeBlockedDate,
  getSlotCapacity, setSlotCapacity, getPass,
} from "../lib/api";
import { toast } from "sonner";
import { Trash2, LogOut, CalendarPlus, Download, MessageCircle, QrCode, ScanLine, X, ShieldCheck } from "lucide-react";

const STORAGE_KEY = "riviera_admin_token";
const SERVICE_LABELS = { individual: "Séance individuelle", deployment: "Déplacement", capacity: "Capacité journée" };
const APPT_STATUSES = [["new", "Nouveau"], ["confirmed", "Confirmé"], ["checked-in", "Entré"], ["checked-out", "Sorti"], ["cancelled", "Annulé"]];
const STATUS_LABELS = Object.fromEntries(APPT_STATUSES);

function downloadCsv(filename, headers, rows) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = "﻿" + [headers.map(esc).join(";"), ...rows.map((r) => r.map(esc).join(";"))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem(STORAGE_KEY) || "");
  const [pass, setPass] = useState("");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) return;
    adminVerify(token).then(() => setAuthed(true)).catch(() => { localStorage.removeItem(STORAGE_KEY); setToken(""); });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { token: t } = await adminLogin(pass);
      localStorage.setItem(STORAGE_KEY, t); setToken(t); setAuthed(true);
    } catch { toast.error("Invalid password"); }
    finally { setLoading(false); }
  };

  if (!authed) {
    return (
      <main className="pt-32 min-h-[80vh]" data-testid="page-admin-locked">
        <div className="container-luxe py-16 max-w-md">
          <div className="overline">ADMIN</div>
          <h1 className="font-display text-4xl text-[var(--ink)] mt-4 font-light">Lead Dashboard</h1>
          <form onSubmit={submit} className="mt-8 space-y-3">
            <input type="password" value={pass} onChange={(e) => setPass(e.target.value)} placeholder="Admin password" className="input-luxe" data-testid="admin-password" />
            <button type="submit" disabled={loading} className="btn-gold w-full" data-testid="admin-submit">Unlock</button>
          </form>
        </div>
      </main>
    );
  }

  return <AdminDashboard token={token} onLogout={() => { localStorage.removeItem(STORAGE_KEY); setAuthed(false); setToken(""); }} />;
}

function AdminDashboard({ token, onLogout }) {
  const [tab, setTab] = useState("leads");

  return (
    <main className="pt-28 pb-24" data-testid="page-admin">
      <div className="container-luxe">
        <div className="flex items-center justify-between hairline-b pb-6">
          <div>
            <div className="overline">ADMIN · CONTROL ROOM</div>
            <h1 className="font-display text-3xl text-[var(--ink)] mt-2 font-light">Dashboard</h1>
          </div>
          <button onClick={onLogout} className="btn-ghost" data-testid="admin-logout"><LogOut size={14} /> Logout</button>
        </div>

        <div className="mt-6 flex items-center gap-2 flex-wrap">
          {[["leads", "Leads"], ["rdv", "Rendez-vous"], ["blocked", "Dates bloquées"]].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`tab-${k}`}
              className={`px-4 py-2 text-[11px] uppercase tracking-[0.18em] font-display border rounded-full transition-colors ${
                tab === k ? "border-[var(--gold)] text-[var(--gold)]" : "border-[var(--ink)]/15 text-[var(--ink)]/60 hover:text-[var(--ink)]"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "leads" && <LeadsPanel token={token} />}
        {tab === "rdv" && <AppointmentsPanel token={token} />}
        {tab === "blocked" && <BlockedPanel token={token} />}
      </div>
    </main>
  );
}

function LeadsPanel({ token }) {
  const [leads, setLeads] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    try {
      const [l, m] = await Promise.all([listLeads(token), adminMetrics(token)]);
      setLeads(l); setMetrics(m);
    } catch { toast.error("Failed to load"); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const filtered = leads.filter((l) => filter === "all" ? true : l.source === filter);
  const sources = Array.from(new Set(leads.map((l) => l.source).filter(Boolean)));

  const changeStatus = async (id, s) => {
    await updateLeadStatus(token, id, s); load();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete lead?")) return;
    await deleteLead(token, id); load();
  };

  const exportCsv = () => {
    downloadCsv(
      `leads-${new Date().toISOString().slice(0, 10)}.csv`,
      ["Créé le", "Nom", "Email", "Téléphone", "Service", "Source", "Statut", "Message"],
      filtered.map((l) => [(l.created_at || "").slice(0, 16).replace("T", " "), l.name, l.email, l.phone || "", l.service || "", l.source || "", l.status || "", (l.message || "").replace(/\s+/g, " ")])
    );
  };

  return (
    <>
      {metrics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px hairline mt-6">
          <div className="bg-surface-1 p-5">
            <div className="overline">Total</div>
            <div className="font-display text-2xl text-[var(--ink)] mt-1 font-light">{metrics.total}</div>
          </div>
          {Object.entries(metrics.by_status || {}).slice(0, 3).map(([k, v]) => (
            <div key={k} className="bg-surface-1 p-5">
              <div className="overline">{k}</div>
              <div className="font-display text-2xl text-[var(--ink)] mt-1 font-light">{v}</div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-8 flex items-center gap-2 flex-wrap">
        <button onClick={() => setFilter("all")} className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] font-display border rounded-full ${filter === "all" ? "border-[var(--gold)] text-[var(--gold)]" : "border-[var(--ink)]/15 text-[var(--ink)]/60"}`} data-testid="filter-all">All</button>
        {sources.map((s) => (
          <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] font-display border rounded-full ${filter === s ? "border-[var(--gold)] text-[var(--gold)]" : "border-[var(--ink)]/15 text-[var(--ink)]/60"}`} data-testid={`filter-${s}`}>{s}</button>
        ))}
        <button onClick={exportCsv} className="btn-ghost ml-auto" data-testid="export-leads-csv"><Download size={13} /> Export CSV</button>
      </div>

      <div className="mt-6 overflow-x-auto hairline" data-testid="leads-table">
        <table className="w-full text-sm">
          <thead className="bg-[var(--surface-2)]">
            <tr className="text-left text-[11px] uppercase tracking-[0.16em] text-[var(--ink)]/60">
              <th className="p-3">Date</th>
              <th className="p-3">Name</th>
              <th className="p-3">Email</th>
              <th className="p-3">Phone</th>
              <th className="p-3">Service</th>
              <th className="p-3">Source</th>
              <th className="p-3">Status</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="p-6 text-center text-[var(--ink)]/40">No leads yet.</td></tr>
            )}
            {filtered.map((l) => (
              <tr key={l.id} className="hairline-t hover:bg-[var(--surface-2)]" data-testid={`lead-row-${l.id}`}>
                <td className="p-3 text-[var(--ink)]/60">{(l.created_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="p-3 text-[var(--ink)]">{l.name}</td>
                <td className="p-3 text-[var(--ink)]/80">{l.email}</td>
                <td className="p-3 text-[var(--ink)]/60">{l.phone || "—"}</td>
                <td className="p-3 text-[var(--ink)]/60">{l.service || "—"}</td>
                <td className="p-3"><span className="text-[10px] uppercase tracking-widest text-[var(--gold)]">{l.source}</span></td>
                <td className="p-3">
                  <select value={l.status || "new"} onChange={(e) => changeStatus(l.id, e.target.value)} className="bg-white border border-[var(--border-subtle)] text-[var(--ink)] text-xs px-2 py-1 rounded" data-testid={`status-${l.id}`}>
                    {["new","contacted","confirmed","archived"].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="p-3 text-right">
                  <button onClick={() => remove(l.id)} className="text-[var(--ink)]/40 hover:text-red-400" data-testid={`delete-${l.id}`}><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function AppointmentsPanel({ token }) {
  const [appts, setAppts] = useState(null);
  const [capacity, setCapacity] = useState(1);
  const [scanOpen, setScanOpen] = useState(false);

  const load = async () => {
    try { setAppts(await listAppointments(token)); }
    catch { toast.error("Failed to load"); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
    getSlotCapacity().then((d) => setCapacity(d.slot_capacity || 1)).catch(() => {});
  }, []);

  const changeStatus = async (id, s) => {
    await updateAppointmentStatus(token, id, s);
    toast.success(s === "confirmed" ? "Confirmé — email + pass QR envoyés au client" : "Statut mis à jour");
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer ce rendez-vous ?")) return;
    await deleteAppointment(token, id); load();
  };
  const changeCapacity = async (n) => {
    if (n === capacity) return;
    await setSlotCapacity(token, n);
    setCapacity(n);
    toast.success(n === 2 ? "2 équipes actives par créneau" : "1 équipe active par créneau");
  };

  const exportCsv = () => {
    downloadCsv(
      `rendez-vous-${new Date().toISOString().slice(0, 10)}.csv`,
      ["Date", "Heure", "Nom", "Email", "Téléphone", "Soin", "Lieu", "Statut", "Pass", "Message"],
      (appts || []).map((a) => [a.date, a.time, a.name, a.email, a.phone || "", SERVICE_LABELS[a.service] || a.service, a.location || "", a.status || "", a.pass_code || "", (a.message || "").replace(/\s+/g, " ")])
    );
  };

  const waHref = (a) => {
    const num = (a.phone || "").replace(/[^0-9]/g, "");
    const msg = encodeURIComponent(
      `Bonjour ${a.name}, Riviera Shockwaves — votre séance (${SERVICE_LABELS[a.service] || a.service}) du ${a.date} à ${a.time} est confirmée. Votre pass : ${a.pass_url || a.pass_code}. Lieu : ${a.location || "à convenir"}. À très vite !`
    );
    return `https://wa.me/${num}?text=${msg}`;
  };

  return (
    <>
      <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] uppercase tracking-[0.18em] font-display text-[var(--ink)]/50">Capacité / créneau</span>
          {[1, 2].map((n) => (
            <button
              key={n}
              onClick={() => changeCapacity(n)}
              data-testid={`capacity-${n}`}
              className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.14em] font-display border rounded-full transition-colors ${
                capacity === n ? "border-[var(--gold)] text-[var(--gold)]" : "border-[var(--ink)]/15 text-[var(--ink)]/60 hover:text-[var(--ink)]"
              }`}
            >
              {n === 1 ? "1 équipe" : "2 équipes"}
            </button>
          ))}
          <button onClick={() => setScanOpen(true)} className="btn-gold !py-2 !px-4" data-testid="open-scanner">
            <ScanLine size={13} /> Scanner un pass
          </button>
        </div>
        <div className="flex items-center gap-2">
          <a href="/qr-reservation.svg" download="qr-reservation-riviera-shockwaves.svg" className="btn-ghost" data-testid="download-booking-qr">
            <QrCode size={13} /> QR Réservation
          </a>
          <button onClick={exportCsv} className="btn-ghost" data-testid="export-appts-csv"><Download size={13} /> Export CSV</button>
        </div>
      </div>

      <div className="mt-4 overflow-x-auto hairline" data-testid="appointments-table">
        <table className="w-full text-sm">
          <thead className="bg-[var(--surface-2)]">
            <tr className="text-left text-[11px] uppercase tracking-[0.16em] text-[var(--ink)]/60">
              <th className="p-3">Date</th>
              <th className="p-3">Heure</th>
              <th className="p-3">Nom</th>
              <th className="p-3">Contact</th>
              <th className="p-3">Soin</th>
              <th className="p-3">Lieu</th>
              <th className="p-3">Pass</th>
              <th className="p-3">Statut</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {appts === null && (
              <tr><td colSpan={9} className="p-6 text-center text-[var(--ink)]/40">Chargement…</td></tr>
            )}
            {appts !== null && appts.length === 0 && (
              <tr><td colSpan={9} className="p-6 text-center text-[var(--ink)]/40">Aucun rendez-vous pour le moment.</td></tr>
            )}
            {(appts || []).map((a) => (
              <tr key={a.id} className="hairline-t hover:bg-[var(--surface-2)]" data-testid={`appt-row-${a.id}`}>
                <td className="p-3 text-[var(--ink)] font-display">{a.date}</td>
                <td className="p-3 text-[var(--ink)] font-display">{a.time}</td>
                <td className="p-3 text-[var(--ink)]">
                  <span className="inline-flex items-center gap-2">
                    {a.name}
                    {a.wa_confirmed_at && (
                      <span
                        className="inline-flex items-center gap-1 text-[9px] font-display font-semibold uppercase tracking-[0.1em] text-emerald-700 border border-emerald-300 bg-emerald-50 rounded-full px-2 py-0.5"
                        title="Confirmé via WhatsApp"
                        data-testid={`appt-wa-ok-${a.id}`}
                      >
                        <MessageCircle size={10} /> WA ✓
                      </span>
                    )}
                  </span>
                </td>
                <td className="p-3 text-[var(--ink)]/70 text-xs">
                  <div>{a.email}</div>
                  {a.phone && <div className="text-[var(--ink)]/50">{a.phone}</div>}
                </td>
                <td className="p-3 text-[var(--ink)]/70">{SERVICE_LABELS[a.service] || a.service}</td>
                <td className="p-3 text-[var(--ink)]/60">{a.location || "—"}</td>
                <td className="p-3 font-mono text-[11px] text-[var(--ink)]/50">{a.pass_code || "—"}</td>
                <td className="p-3">
                  <select
                    value={a.status || "new"}
                    onChange={(e) => changeStatus(a.id, e.target.value)}
                    className={`bg-white border text-xs px-2 py-1 rounded ${
                      a.status === "confirmed" || a.status === "checked-in" ? "border-[var(--gold)] text-[var(--gold)]"
                      : a.status === "cancelled" ? "border-red-300 text-red-400"
                      : "border-[var(--border-subtle)] text-[var(--ink)]"
                    }`}
                    data-testid={`appt-status-${a.id}`}
                  >
                    {APPT_STATUSES.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
                  </select>
                </td>
                <td className="p-3 text-right">
                  <div className="inline-flex items-center gap-3">
                    {a.phone && (
                      <a href={waHref(a)} target="_blank" rel="noopener noreferrer" className="text-[var(--ink)]/40 hover:text-[var(--gold)] transition-colors" data-testid={`appt-wa-${a.id}`} aria-label="WhatsApp client">
                        <MessageCircle size={15} />
                      </a>
                    )}
                    <button onClick={() => remove(a.id)} className="text-[var(--ink)]/40 hover:text-red-400" data-testid={`appt-delete-${a.id}`}><Trash2 size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {scanOpen && <PassScanner token={token} onClose={() => { setScanOpen(false); load(); }} />}
    </>
  );
}

function PassScanner({ token, onClose }) {
  const [code, setCode] = useState("");
  const [pass, setPass] = useState(null);
  const [err, setErr] = useState("");
  const [camOn, setCamOn] = useState(false);
  const videoRef = useRef(null);
  const rafRef = useRef(0);
  const camSupported = typeof window !== "undefined" && "BarcodeDetector" in window && !!navigator.mediaDevices;

  const stopCam = () => {
    cancelAnimationFrame(rafRef.current);
    const v = videoRef.current;
    if (v && v.srcObject) v.srcObject.getTracks().forEach((tr) => tr.stop());
    setCamOn(false);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => stopCam(), []);

  const lookup = async (raw) => {
    const val = String(raw ?? code).trim();
    if (!val) return;
    const m = val.match(/RS-\d{8}-[A-Z0-9]{6}/i);
    const c2 = (m ? m[0] : val).toUpperCase();
    setErr("");
    setPass(null);
    try {
      setPass(await getPass(c2));
    } catch {
      setErr("Pass introuvable. Vérifiez le code.");
    }
  };

  const startCam = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      videoRef.current.srcObject = stream;
      await videoRef.current.play();
      setCamOn(true);
      const bd = new window.BarcodeDetector({ formats: ["qr_code"] });
      const tick = async () => {
        if (!videoRef.current) return;
        try {
          const codes = await bd.detect(videoRef.current);
          if (codes && codes.length) {
            stopCam();
            lookup(codes[0].rawValue);
            return;
          }
        } catch {}
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch {
      setErr("Caméra indisponible — saisissez le code manuellement.");
    }
  };

  const nextAction = pass
    ? { confirmed: ["checked-in", "Valider l'entrée"], "checked-in": ["checked-out", "Valider la sortie"] }[pass.status] || null
    : null;

  const advance = async () => {
    if (!nextAction || !pass) return;
    await updateAppointmentStatus(token, pass.id, nextAction[0]);
    toast.success(nextAction[0] === "checked-in" ? "Entrée validée" : "Sortie validée");
    lookup(pass.pass_code);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-[var(--ink)]/45 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="pass-scanner">
      <div className="bg-[var(--surface-1)] hairline max-w-md w-full p-6 md:p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="overline">CONTRÔLE D'ACCÈS</div>
          <button onClick={onClose} className="text-[var(--ink)]/40 hover:text-[var(--ink)]" data-testid="scanner-close" aria-label="Fermer"><X size={18} /></button>
        </div>
        <h2 className="font-display text-2xl text-[var(--ink)] mt-2 font-light">Scanner ou saisir un pass</h2>

        <div className="mt-5 flex gap-2">
          <input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && lookup()}
            placeholder="RS-20261001-ABC123 ou lien du pass"
            className="input-luxe flex-1"
            data-testid="scanner-input"
          />
          <button onClick={() => lookup()} className="btn-gold !px-4" data-testid="scanner-lookup">OK</button>
        </div>

        {camSupported && (
          <div className="mt-4">
            {!camOn ? (
              <button onClick={startCam} className="btn-ghost w-full" data-testid="scanner-camera">
                <ScanLine size={14} /> Activer la caméra
              </button>
            ) : (
              <video ref={videoRef} playsInline muted className="w-full rounded-md hairline" data-testid="scanner-video" />
            )}
          </div>
        )}

        {err && <p className="mt-4 text-sm text-red-400" data-testid="scanner-error">{err}</p>}

        {pass && (
          <div className="mt-5 hairline p-5 bg-[var(--bg-base)]" data-testid="scanner-result">
            <div className="flex items-center justify-between">
              <span className="font-display text-lg text-[var(--ink)]">{pass.name}</span>
              <span className={`text-[10px] uppercase tracking-[0.18em] font-display font-semibold px-2.5 py-1 rounded-full border ${
                pass.status === "cancelled" ? "border-red-300 text-red-400" : "border-[var(--gold)]/40 text-[var(--gold)]"
              }`} data-testid="scanner-status">
                {STATUS_LABELS[pass.status] || pass.status}
              </span>
            </div>
            <div className="mt-2 text-sm text-[var(--ink)]/70">
              {SERVICE_LABELS[pass.service] || pass.service} · {pass.date} · {pass.time}
            </div>
            {pass.location && <div className="text-xs text-[var(--ink)]/50 mt-1">{pass.location}</div>}
            <div className="mt-2 font-mono text-[11px] text-[var(--ink)]/40">{pass.pass_code}</div>
            {nextAction && (
              <button onClick={advance} className="btn-gold w-full mt-4" data-testid="scanner-advance">
                <ShieldCheck size={14} /> {nextAction[1]}
              </button>
            )}
            {pass.status === "new" && <p className="mt-3 text-xs text-[var(--ink)]/50">Réservation non confirmée — passez-la d'abord en « Confirmé ».</p>}
            {pass.status === "cancelled" && <p className="mt-3 text-xs text-red-400">Pass annulé — accès refusé.</p>}
            {pass.status === "checked-out" && <p className="mt-3 text-xs text-[var(--ink)]/50">Client déjà sorti.</p>}
          </div>
        )}
      </div>
    </div>
  );
}

function BlockedPanel({ token }) {
  const [items, setItems] = useState(null);
  const [date, setDate] = useState("");

  const load = async () => {
    try { setItems(await listBlockedDates(token)); }
    catch { toast.error("Failed to load"); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!date) return;
    await addBlockedDate(token, date);
    setDate("");
    toast.success("Date bloquée");
    load();
  };
  const remove = async (id) => {
    await removeBlockedDate(token, id); load();
  };

  return (
    <div className="mt-6 max-w-xl">
      <p className="text-sm text-[var(--text-secondary)] mb-5">
        Les jours bloqués apparaissent comme indisponibles dans le planning de réservation (fermeture, événement privé, équipe complète…).
      </p>
      <div className="flex items-center gap-3">
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="input-luxe max-w-[220px]"
          data-testid="blocked-input"
        />
        <button onClick={add} disabled={!date} className="btn-gold" data-testid="blocked-add">
          <CalendarPlus size={14} /> Bloquer
        </button>
      </div>
      <div className="mt-6 hairline" data-testid="blocked-list">
        {items === null && <div className="p-5 text-center text-[var(--ink)]/40 text-sm">Chargement…</div>}
        {items !== null && items.length === 0 && (
          <div className="p-5 text-center text-[var(--ink)]/40 text-sm">Aucune date bloquée.</div>
        )}
        {(items || []).map((b) => (
          <div key={b.id} className="flex items-center justify-between p-4 hairline-t first:border-t-0 bg-surface-1" data-testid={`blocked-row-${b.id}`}>
            <span className="font-display text-sm text-[var(--ink)]">{b.date}</span>
            <button onClick={() => remove(b.id)} className="text-[var(--ink)]/40 hover:text-red-400" data-testid={`blocked-delete-${b.id}`}><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
